import { useMemo } from "react";

import { useGetNotifications } from "../../../api/insights";
import { RankedBarChart } from "../charts";
import SectionShell, {
  Stat,
  StatGrid,
  formatNum,
  formatPct,
} from "../SectionShell";

function Notifications({ eventId }) {
  const { data, isPending, isError, error } = useGetNotifications(eventId);
  const email = data?.email;
  const push = data?.push;

  const hasEmail = (email?.sent ?? 0) > 0;
  const hasPush = (push?.totalRecipients ?? 0) > 0;

  const chartData = useMemo(
    () => [
      ...(hasEmail
        ? [
            {
              type: "Email campaigns",
              Sent: email?.sent ?? 0,
              Delivered: email?.delivered ?? 0,
              Opened: email?.opened ?? 0,
            },
          ]
        : []),
      ...(hasPush
        ? [
            {
              type: "Notifications",
              Sent: push?.totalRecipients ?? 0,
              Opened: push?.totalOpens ?? 0,
            },
          ]
        : []),
    ],
    [
      hasEmail,
      hasPush,
      email?.sent,
      email?.delivered,
      email?.opened,
      push?.totalRecipients,
      push?.totalOpens,
    ]
  );

  const title =
    hasEmail && !hasPush
      ? "Campaigns"
      : hasPush && !hasEmail
        ? "Notifications"
        : "Campaigns and notifications";
  const description =
    hasEmail && !hasPush
      ? "Email campaign performance for this event."
      : hasPush && !hasEmail
        ? "Push notification performance for this event."
        : "Email campaign and push notification performance for this event.";

  return (
    <SectionShell
      id="notifications"
      title={title}
      description={description}
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="notifications"
    >
      <StatGrid>
        {hasEmail && (
          <Stat
            part="emailSent"
            label="Email sent"
            value={formatNum(email?.sent)}
          />
        )}
        {hasEmail && (
          <Stat
            part="emailOpenRate"
            label="Email open rate"
            value={formatPct(email?.openRate)}
          />
        )}
        {hasPush && (
          <Stat
            part="notificationsSent"
            label="Notifications sent"
            value={formatNum(push?.totalRecipients)}
          />
        )}
        {hasPush && (
          <Stat
            part="notificationOpenRate"
            label="Notification open rate"
            value={formatPct(push?.openRate)}
          />
        )}
      </StatGrid>

      <RankedBarChart
        part="byType"
        title="By type"
        data={chartData}
        categoryKey="type"
        series={[
          { name: "Sent", color: "cyan.6" },
          ...(hasEmail ? [{ name: "Delivered", color: "blue.6" }] : []),
          { name: "Opened", color: "teal.6" },
        ]}
      />
    </SectionShell>
  );
}

export default Notifications;
